import { computed, inject, Injectable, signal } from '@angular/core';
import { MessagesService } from '@pressteaze/ui';
import {
	ConfigurationApiError,
	ConfigurationApiService,
} from './configuration-api.service';
import type { ConfigurationContext } from './configuration.models';

const errorMessages: Record<string, string> = {
	FORBIDDEN: 'Δεν έχετε δικαίωμα για αυτή την ενέργεια.',
	OWNER_ONLY: 'Η μεταβίβαση μπορεί να γίνει μόνο από τον ιδιοκτήτη.',
	INVALID_STORE_ASSIGNMENT:
		'Το προεπιλεγμένο κατάστημα πρέπει να είναι ανατεθειμένο στον χρήστη.',
	SCHEDULE_OVERLAP: 'Τα χρονικά διαστήματα επικαλύπτονται.',
	OPENING_HOURS_OVERLAP: 'Τα ωράρια της ίδιας ημέρας επικαλύπτονται.',
	ONLINE_BOOKING_HOURS_OVERLAP:
		'Τα online διαστήματα της ίδιας ημέρας επικαλύπτονται.',
	ONLINE_BOOKING_EXCEPTION_WINDOWS_OVERLAP:
		'Τα online διαστήματα της εξαίρεσης επικαλύπτονται.',
	ONLINE_BOOKING_OUTSIDE_OPERATING_HOURS:
		'Οι online ώρες έναρξης πρέπει να χωρούν πλήρως μέσα στο ωράριο λειτουργίας.',
	DNS_PROOF_NOT_FOUND: 'Δεν βρέθηκε ακόμη το αναμενόμενο DNS TXT record.',
	CHALLENGE_EXPIRED: 'Το DNS challenge έληξε. Δημιουργήστε το domain ξανά.',
	PIN_ALREADY_IN_USE: 'Το PIN χρησιμοποιείται ήδη από ενεργό χρήστη.',
	CONFIGURATION_IDENTITY_REQUIRED:
		'Απαιτείται λογαριασμός σύνδεσης για αυτόν τον ρόλο.',
};

@Injectable({ providedIn: 'root' })
export class ConfigurationStateService {
	private readonly api = inject(ConfigurationApiService);
	private readonly messagesSrv = inject(MessagesService);
	private readonly contextState = signal<ConfigurationContext | null>(null);
	readonly context = this.contextState.asReadonly();
	readonly loading = signal(false);
	readonly selectedStore = computed(() => {
		const context = this.contextState();
		return (
			context?.stores.find((store) => store.id === context.selected_store_id) ??
			null
		);
	});

	async load(storeId?: string): Promise<void> {
		this.loading.set(true);
		try {
			this.contextState.set(await this.api.context(storeId));
		} catch (error) {
			this.showError(error);
		} finally {
			this.loading.set(false);
		}
	}

	async run(
		body: Record<string, unknown>,
		success: string,
		userCommand = false,
	): Promise<boolean> {
		this.loading.set(true);
		try {
			if (userCommand) await this.api.userCommand(body);
			else await this.api.command(body);
			this.messagesSrv.success(success);
			await this.load(this.contextState()?.selected_store_id ?? undefined);
			return true;
		} catch (error) {
			this.showError(error);
			return false;
		} finally {
			this.loading.set(false);
		}
	}

	private showError(error: unknown): void {
		const code =
			error instanceof ConfigurationApiError ? error.code : 'REQUEST_FAILED';
		this.messagesSrv.error(
			'Η ενέργεια δεν ολοκληρώθηκε',
			errorMessages[code] ?? `Κωδικός: ${code}`,
		);
	}
}
